"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Card,
  Group,
  Badge,
  SimpleGrid,
  Paper,
  Button,
  Flex,
  Divider,
  Image,
  Grid,
  ThemeIcon,
  Table,
  List,
  Tabs,
} from "@mantine/core";
import {
  IconPalette,
  IconArrowRight,
  IconEye,
  IconSparkles,
  IconX,
  IconColorSwatch,
  IconPhoto,
  IconDroplet,
} from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { colorSeasons } from "@/constants";
import OutfitGrid from "@/components/OutfitGrid";
import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";

interface OutfitIdea {
  title: string;
  description: string;
  imageData: string;
}

export default function TrueSpringVsBrightSpringPaletteContent() {
  const t = useTranslations("trueSpringVsBrightSpringPalette");

  // Get the subseason data
  const trueSpringData = colorSeasons.SPRING.subseasons.TRUE;
  const brightSpringData = colorSeasons.SPRING.subseasons.BRIGHT;

  // State for outfit data
  const [trueSpringOutfits, setTrueSpringOutfits] = useState<OutfitIdea[]>([]);
  const [brightSpringOutfits, setBrightSpringOutfits] = useState<OutfitIdea[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch outfit data
  useEffect(() => {
    const fetchOutfitData = async () => {
      try {
        const [trueSpringDoc, brightSpringDoc] = await Promise.all([
          getDoc(doc(db, "outfitIdeas", "SPRING_TRUE")),
          getDoc(doc(db, "outfitIdeas", "SPRING_BRIGHT")),
        ]);

        setTrueSpringOutfits(trueSpringDoc.data()?.ideas || []);
        setBrightSpringOutfits(brightSpringDoc.data()?.ideas || []);
      } catch (error) {
        console.error("Error fetching outfit data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOutfitData();
  }, []);

  const colorComparison = [
    {
      category: "Primary Colors",
      trueSpring: trueSpringData.primaryColors.slice(0, 6),
      brightSpring: brightSpringData.primaryColors.slice(0, 6),
    },
    {
      category: "Neutrals",
      trueSpring: trueSpringData.neutrals.slice(0, 4),
      brightSpring: brightSpringData.neutrals.slice(0, 4),
    },
    {
      category: "Complementary Colors",
      trueSpring: trueSpringData.complementaryColors.slice(0, 4),
      brightSpring: brightSpringData.complementaryColors.slice(0, 4),
    },
  ];

  const stylingTips = [
    {
      aspect: "Makeup",
      trueSpring: "Warm, clear colors with golden undertones. Coral and peach lipsticks work well.",
      brightSpring: "Bright, saturated colors with high contrast. Fuchsia and bright coral lipsticks.",
    },
    {
      aspect: "Jewelry",
      trueSpring: "Gold jewelry with warm stones like amber, coral, and citrine.",
      brightSpring: "Both gold and silver work, but bright stones like emerald and ruby are ideal.",
    },
    {
      aspect: "Hair Colors",
      trueSpring: "Warm golden blondes, auburn, and warm browns with golden highlights.",
      brightSpring: "Bright, clear colors - platinum blonde, bright auburn, or jet black.",
    },
    {
      aspect: "Accessories",
      trueSpring: "Warm-toned bags and shoes in camel, gold, and warm browns.",
      brightSpring: "High-contrast accessories in bright colors or classic black and white.",
    },
  ];

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            True Spring vs Bright Spring Palette
          </Title>
          <Text size="lg" c="dimmed" maw={600} mx="auto" mb="xl">
            Dive deep into the color palettes of True Spring and Bright Spring. Compare specific colors, explore styling
            tips, and discover which palette brings out your natural beauty.
          </Text>

          {/* Season Moodboards Comparison */}
          <Box mb="xl">
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
              <Box>
                <Text fw={500} mb="sm" ta="center" c="green">
                  True Spring Moodboard
                </Text>
                <Box
                  mih={{ base: "200px", md: "300px" }}
                  style={{
                    position: "relative",
                    borderRadius: "1rem",
                    border: "2px solid var(--mantine-color-green-2)",
                  }}
                >
                  <Image
                    src="/SPRING.webp"
                    alt="True Spring season moodboard"
                    width={400}
                    height={300}
                    style={{
                      borderRadius: "1rem",
                      objectFit: "cover",
                      objectPosition: "center",
                    }}
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                </Box>
              </Box>
              <Box>
                <Text fw={500} mb="sm" ta="center" c="green">
                  Bright Spring Moodboard
                </Text>
                <Box
                  mih={{ base: "200px", md: "300px" }}
                  style={{
                    position: "relative",
                    borderRadius: "1rem",
                    border: "2px solid var(--mantine-color-green-2)",
                  }}
                >
                  <Image
                    src="/SPRING.webp"
                    alt="Bright Spring season moodboard"
                    width={400}
                    height={300}
                    style={{
                      borderRadius: "1rem",
                      objectFit: "cover",
                      objectPosition: "center",
                    }}
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                </Box>
              </Box>
            </SimpleGrid>
          </Box>
        </Box>

        {/* Quick Palette Overview */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Palette Overview
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{trueSpringData.emoji}</Text>
                <Title order={3}>True Spring Palette</Title>
                <Text size="sm" c="dimmed" mb="md">
                  Clear, vibrant colors with warm undertones and balanced saturation
                </Text>
                <Badge color="green" variant="light" size="lg">
                  Clear & Radiant
                </Badge>
              </Stack>
            </Paper>
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{brightSpringData.emoji}</Text>
                <Title order={3}>Bright Spring Palette</Title>
                <Text size="sm" c="dimmed" mb="md">
                  High-contrast, saturated colors with maximum vibrancy and energy
                </Text>
                <Badge color="green" variant="light" size="lg">
                  Bold & Vibrant
                </Badge>
              </Stack>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* Detailed Color Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Detailed Color Palette Comparison
          </Title>
          <Stack gap="xl">
            {colorComparison.map((section, sectionIndex) => (
              <Box key={sectionIndex}>
                <Title order={3} mb="md" ta="center" c="green">
                  {section.category}
                </Title>
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                  {/* True Spring Colors */}
                  <Box>
                    <Text fw={500} mb="sm" c="green">
                      True Spring
                    </Text>
                    <SimpleGrid cols={3} spacing="xs">
                      {section.trueSpring.map((color, index) => (
                        <Box
                          key={index}
                          style={{
                            backgroundColor: color.hex,
                            height: 50,
                            borderRadius: 8,
                            border: "2px solid #e0e0e0",
                            position: "relative",
                          }}
                          title={`${color.name} - ${color.hex}`}
                        >
                          <Text
                            size="xs"
                            fw={500}
                            ta="center"
                            style={{
                              position: "absolute",
                              bottom: 2,
                              left: 2,
                              right: 2,
                              backgroundColor: "rgba(255,255,255,0.9)",
                              padding: "2px 4px",
                              borderRadius: 4,
                            }}
                          >
                            {color.name}
                          </Text>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Box>

                  {/* Bright Spring Colors */}
                  <Box>
                    <Text fw={500} mb="sm" c="green">
                      Bright Spring
                    </Text>
                    <SimpleGrid cols={3} spacing="xs">
                      {section.brightSpring.map((color, index) => (
                        <Box
                          key={index}
                          style={{
                            backgroundColor: color.hex,
                            height: 50,
                            borderRadius: 8,
                            border: "2px solid #e0e0e0",
                            position: "relative",
                          }}
                          title={`${color.name} - ${color.hex}`}
                        >
                          <Text
                            size="xs"
                            fw={500}
                            ta="center"
                            style={{
                              position: "absolute",
                              bottom: 2,
                              left: 2,
                              right: 2,
                              backgroundColor: "rgba(255,255,255,0.9)",
                              padding: "2px 4px",
                              borderRadius: 4,
                            }}
                          >
                            {color.name}
                          </Text>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Box>
                </SimpleGrid>
              </Box>
            ))}
          </Stack>
        </Card>

        {/* Styling Tips Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Styling Tips & Recommendations
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {stylingTips.map((tip, index) => (
              <Paper key={index} withBorder p="lg" radius="md">
                <Stack gap="md">
                  <Group>
                    <ThemeIcon color="green" variant="light" size="lg">
                      <IconColorSwatch size={20} />
                    </ThemeIcon>
                    <Text fw={600} size="lg">
                      {tip.aspect}
                    </Text>
                  </Group>
                  <Box>
                    <Text fw={500} c="green" size="sm" mb="xs">
                      True Spring:
                    </Text>
                    <Text size="sm" mb="md">
                      {tip.trueSpring}
                    </Text>
                    <Text fw={500} c="green" size="sm" mb="xs">
                      Bright Spring:
                    </Text>
                    <Text size="sm">{tip.brightSpring}</Text>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </Card>

        {/* Color Temperature Analysis */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Color Temperature & Intensity Analysis
          </Title>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Aspect</Table.Th>
                <Table.Th>True Spring</Table.Th>
                <Table.Th>Bright Spring</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td fw={500}>Temperature</Table.Td>
                <Table.Td>Warm undertones with clear, bright characteristics</Table.Td>
                <Table.Td>Warm undertones with high-contrast, saturated characteristics</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td fw={500}>Intensity</Table.Td>
                <Table.Td>Clear and vibrant, but balanced</Table.Td>
                <Table.Td>Maximum intensity and saturation</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td fw={500}>Contrast</Table.Td>
                <Table.Td>Medium to high contrast</Table.Td>
                <Table.Td>Very high contrast</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td fw={500}>Best Colors</Table.Td>
                <Table.Td>Coral, warm pink, emerald green, golden yellow</Table.Td>
                <Table.Td>Fuchsia, bright orange, electric blue, lime green</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td fw={500}>Avoid Colors</Table.Td>
                <Table.Td>Muted, dusty colors</Table.Td>
                <Table.Td>Muted, soft colors</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Card>

        {/* How to Tell at Home */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            How to Tell the Difference at Home
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h4" mb="md">The Intensity Test</Title>
              <Text size="sm" c="dimmed" mb="sm">
                Compare a warm coral vs an electric orange near your face:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>True Spring:</strong> The warm coral feels balanced and flattering</List.Item>
                <List.Item><strong>Bright Spring:</strong> The electric orange makes you glow intensely</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h4" mb="md">The White Test</Title>
              <Text size="sm" c="dimmed" mb="sm">
                Compare ivory white vs bright pure white:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>True Spring:</strong> Ivory/cream white feels more natural</List.Item>
                <List.Item><strong>Bright Spring:</strong> Bright white doesn't overwhelm you</List.Item>
              </List>
            </Box>
          </SimpleGrid>
          <Paper withBorder p="md" radius="md" mt="lg" bg="gray.0">
            <Text size="sm" c="dimmed">
              Still unsure? These tests can be tricky in artificial lighting or with makeup on. A photo analysis removes the guesswork.
            </Text>
          </Paper>
        </Card>

        {/* Final CTA */}
        <Card withBorder p="xl" radius="md" bg="green.0">
          <Stack gap="lg" ta="center">
            <Title order={2} size="h2">
              Want a Definitive Answer?
            </Title>
            <Text size="lg" c="dimmed" maw={500} mx="auto">
              Upload a photo and get a confident result — no more guessing between True Spring and Bright Spring.
            </Text>
            <Flex gap="md" justify="center" wrap="wrap">
              <Button
                size="lg"
                component={Link}
                href="/ai-color-analysis"
                color="violet"
                rightSection={<IconArrowRight size={20} />}
              >
                Confirm My Season
              </Button>
              <Button
                size="lg"
                variant="outline"
                component={Link}
                href="/color-analysis-quiz"
                color="violet"
              >
                Start With Free Quiz
              </Button>
            </Flex>
            <Group justify="center" gap="xl" mt="md">
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/spring/true"
                size="sm"
                color="gray"
              >
                View True Spring Palette →
              </Button>
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/spring/bright"
                size="sm"
                color="gray"
              >
                View Bright Spring Palette →
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
